import { type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { parseISO } from 'date-fns'
import { LucideMail, LucidePhone, LucideSmartphone } from 'lucide-react'
import { FacebookIcon, LinkedinIcon, XIcon } from 'react-share'
import { ListingCardBase } from '~/components'
import { Button } from '~/components/ui'
import { UserRole, type Listing, type User } from '~/models'
import { activityHelper, findListings, findUser } from '~/mongo.server'
import { fmtDate, fmtName, fmtPhoneNumber, getBrokerMemberships } from '~/utils'
import { getOrganizationSlug } from '~/utils.server'

export const loader = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<{ broker: User; listings: Listing[] }> => {
  const slug = params.slug
  if (!slug) {
    throw new Response('Broker Not found', { status: 404 })
  }

  const organizationSlug = getOrganizationSlug(request)

  const broker = await findUser({ slug, 'organization.slug': organizationSlug })
  if (
    !broker ||
    (broker.role !== UserRole.broker && broker.role !== UserRole.managingBroker)
  ) {
    throw new Response('Not Found', { status: 404 })
  }

  const listingsResult = await findListings({
    find: {
      'organization.slug': organizationSlug,
      'brokers._id': broker._id,
      status: { $in: ['active'] },
    },
    limit: 12,
  })
  if (!listingsResult) {
    throw new Response('Not Found', { status: 404 })
  }
  await activityHelper(request)
    .source('app')
    .type('view')
    .related('broker', broker)
    .write()
  return {
    broker,
    listings: listingsResult!.listings,
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title = data
  if (title) {
    return [
      {
        title: `${fmtName(data.broker.firstName, data.broker.middleName, data.broker.lastName)} - Associate`,
      },
    ]
  }
  return []
}

export default function AppBrokerDetailRoute() {
  const { broker, listings } = useLoaderData<typeof loader>()
  return (
    <>
      <div className="mx-auto max-w-5xl px-4 pt-16 sm:px-6">
        <div className="mb-6 flex gap-6">
          {broker.meta?.photo?.url && (
            <div>
              <img
                className="max-w-[100px] rounded-md shadow-md"
                src={broker.meta.photo?.url}
                alt="Broker Photo"></img>
            </div>
          )}
          <div className="">
            <h1 className="mb-2 text-2xl tracking-tight sm:text-4xl">
              {fmtName(broker.firstName, broker.middleName, broker.lastName)}
              {broker.meta?.memberships && (
                <span className="ml-2 text-xl tracking-normal text-muted-foreground">
                  {getBrokerMemberships(broker)}
                </span>
              )}
            </h1>
            <h3 className="mb-6 text-xl tracking-tight">
              {broker.meta?.title}
            </h3>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-10 gap-y-6 lg:mx-0 lg:grid-cols-3">
          {broker.company && (
            <div className="">
              <dt className="mb-1 font-medium text-muted-foreground">
                Company
              </dt>
              <dd className="whitespace-pre-line">
                <Button
                  asChild
                  variant="link"
                  className="px-0 text-base font-normal">
                  <Link to={`/companies/${broker.company.slug}`}>
                    {broker.company.name}
                  </Link>
                </Button>
              </dd>
            </div>
          )}
          {broker.email && (
            <div className="">
              <dt className="mb-1 font-medium text-muted-foreground">Email</dt>
              <dd className="flex items-center">
                <LucideMail className="mr-3 hidden h-5 w-5 text-foreground md:block" />
                <Button
                  asChild
                  variant="link"
                  className="px-0 text-base font-normal">
                  <a href={`mailto:${broker.email}`} target="_blank">
                    {broker.email}
                  </a>
                </Button>
              </dd>
            </div>
          )}
          {broker.meta?.website && (
            <div className="">
              <dt className="mb-1 font-medium text-muted-foreground">
                Website
              </dt>
              <dd className="">
                <Button
                  asChild
                  variant="link"
                  className="px-0 text-base font-normal">
                  <a
                    href={broker.meta.website}
                    target="_blank"
                    className="text-primary">
                    {broker.meta.website}
                  </a>
                </Button>
              </dd>
            </div>
          )}
          {(broker.meta?.linkedIn ||
            broker.meta?.facebook ||
            broker.meta?.twitter) && (
            <div className="">
              <dt className="mb-1 font-medium text-muted-foreground">
                Socials
              </dt>
              <div className="flex gap-x-2">
                {broker.meta?.linkedIn && (
                  <dd>
                    <a
                      href={broker.meta.linkedIn}
                      target="_blank"
                      className="text-primary">
                      <LinkedinIcon
                        size={36}
                        borderRadius={10}
                        //className="px-0 text-base font-normal"
                      />
                    </a>
                  </dd>
                )}
                {broker.meta?.facebook && (
                  <dd>
                    <a
                      href={broker.meta.facebook}
                      target="_blank"
                      className="text-primary">
                      <FacebookIcon
                        size={36}
                        borderRadius={10}
                        //className="px-0 text-base font-normal"
                      />
                    </a>
                  </dd>
                )}
                {broker.meta?.twitter && (
                  <dd>
                    <a
                      href={broker.meta.twitter}
                      target="_blank"
                      className="text-primary">
                      <XIcon
                        size={36}
                        borderRadius={10}
                        //className="px-0 text-base font-normal"
                      />
                    </a>
                  </dd>
                )}
              </div>
            </div>
          )}

          {broker.meta?.office && (
            <div className="">
              <dt className="mb-1 font-medium text-muted-foreground">Office</dt>
              <dd className="flex items-center">
                <LucidePhone className="mr-3 hidden h-5 w-5 text-foreground md:block" />
                <Button
                  asChild
                  variant="link"
                  className="px-0 text-base font-normal">
                  <a href={`tel:${broker.meta.office}`} target="_blank">
                    {fmtPhoneNumber(broker.meta.office)}
                  </a>
                </Button>
              </dd>
            </div>
          )}
          {broker.meta?.mobile && (
            <div className="">
              <dt className="mb-1 font-medium text-muted-foreground">Mobile</dt>
              <dd className="flex items-center">
                <LucideSmartphone className="mr-3 hidden h-5 w-5 text-foreground md:block" />
                <Button
                  asChild
                  variant="link"
                  className="px-0 text-base font-normal">
                  <a href={`tel:${broker.meta.mobile}`} target="_blank">
                    {fmtPhoneNumber(broker.meta.mobile)}
                  </a>
                </Button>
              </dd>
            </div>
          )}
        </dl>

        {broker.meta?.description && (
          <div
            className="prose mt-6  max-w-none whitespace-pre-line text-base leading-7"
            dangerouslySetInnerHTML={{
              __html: broker.meta!.description!,
            }}></div>
        )}
        {broker.meta?.memberSince && (
          <div className="prose mt-6 italic">
            {broker.organization.name} member since{' '}
            {fmtDate(parseISO(broker.meta.memberSince!))}
          </div>
        )}
      </div>
      {listings.length > 0 && (
        <div className="mt-16 bg-muted px-4 py-16 md:px-6 lg:px-8">
          <div className="mx-auto max-w-screen-2xl">
            <h2 className="mb-16 text-center text-2xl sm:text-3xl">
              Latest Listings
            </h2>
            <div className="3xl:grid-cols-5 grid grid-cols-1 place-content-start gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4">
              {listings.map((listing) => {
                return (
                  <ListingCardBase
                    key={listing._id}
                    listing={listing}
                    showCompany={false}
                  />
                )
              })}
            </div>
            <div className="my-8 flex justify-center">
              <Button asChild>
                <Link
                  to={`/cards?types=sale_lease&statuses=active&brokers=${broker.tid}`}>
                  View All Listings
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
